'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { styles } from './UiToolSliderArrowCursor.styles';

type Direction = 'left' | 'right';
type Zone = Direction | 'center';

const EDGE_ZONE_WIDTH = 0.2;
const INTERACTIVE_SELECTOR = 'a, button, input, textarea, select, label, [role="button"]';

export interface UiToolSliderArrowCursorProps {
  className?: string;
  children?: React.ReactNode;
  enabled?: boolean;
  navigateOnClick?: boolean;
  onNavigate?: (direction: Direction) => void;
}

export function UiToolSliderArrowCursor({
  className,
  children,
  enabled = true,
  navigateOnClick = false,
  onNavigate
}: UiToolSliderArrowCursorProps) {
  const slots = styles();
  const [isHovering, setIsHovering] = useState(false);
  const [zone, setZone] = useState<Zone>('center');
  const [direction, setDirection] = useState<Direction>('right');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const pointerPositionRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);

  const showArrowCursor = enabled && isHovering && zone !== 'center';

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      document.body.style.removeProperty('cursor');
    };
  }, []);

  useEffect(() => {
    if (showArrowCursor) {
      document.body.style.setProperty('cursor', 'none');
      return;
    }

    document.body.style.removeProperty('cursor');
  }, [showArrowCursor]);

  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return Boolean(target.closest(INTERACTIVE_SELECTOR));
  };

  const updateCursorPosition = () => {
    animationFrameRef.current = null;

    if (!cursorRef.current) {
      return;
    }

    const { x, y } = pointerPositionRef.current;
    cursorRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
  };

  const getZoneByClientX = (clientX: number): Zone => {
    if (!wrapperRef.current) {
      return 'center';
    }

    const rect = wrapperRef.current.getBoundingClientRect();
    const relativeX = (clientX - rect.left) / rect.width;

    if (relativeX <= EDGE_ZONE_WIDTH) {
      return 'left';
    }

    if (relativeX >= 1 - EDGE_ZONE_WIDTH) {
      return 'right';
    }

    return 'center';
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled || !navigateOnClick || !onNavigate) {
      return;
    }

    const target = event.target as HTMLElement;

    if (target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }

    const nextZone = getZoneByClientX(event.clientX);

    if (nextZone === 'left') {
      onNavigate('left');
      return;
    }

    if (nextZone === 'right') {
      onNavigate('right');
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled || !wrapperRef.current) {
      return;
    }

    setIsHovering((prev) => (prev ? prev : true));

    pointerPositionRef.current = { x: event.clientX, y: event.clientY };
    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(updateCursorPosition);
    }

    const nextZoneByPosition = getZoneByClientX(event.clientX);
    const nextZone = isInteractiveTarget(event.target) ? 'center' : nextZoneByPosition;

    setZone((prevZone) => (prevZone === nextZone ? prevZone : nextZone));

    if (nextZone !== 'center') {
      const nextDirection: Direction = nextZone;
      setDirection((prevDirection) =>
        prevDirection === nextDirection ? prevDirection : nextDirection
      );
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={slots.base({ class: className, enabled: showArrowCursor })}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      onMouseLeave={() => {
        setIsHovering(false);
        setZone('center');

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }}
    >
      {children}

      <div
        ref={cursorRef}
        className={slots.cursor({ visible: showArrowCursor })}
        aria-hidden
      >
        <Image
          src={direction === 'left' ? '/icons/arrow-left.svg' : '/icons/arrow-right.svg'}
          alt=""
          width={36}
          height={36}
          className={slots.icon()}
        />
      </div>
    </div>
  );
}
